import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import {
  type CreatePatientRequest,
  createPatientRequestSchema,
  type Patient,
  patientResponseSchema,
} from "../types";
import { getPatientQueryKey } from "./usePatient";

export function useCreatePatient(
  options: UseMutationOptions<Patient, string, CreatePatientRequest> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  return useMutation({
    mutationFn: async (params) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/patients`,
        data: params,
        requestSchema: createPatientRequestSchema,
        responseSchema: patientResponseSchema,
      });

      return response.data.data;
    },
    onSuccess: (newPatient) => {
      logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_PATIENT_CREATED, {
        workplaceId,
        patientId: newPatient.id,
        performedByAdmin: isAdmin,
      });
      queryClient.setQueryData(
        getPatientQueryKey({ workplaceId, patientId: newPatient.id }),
        newPatient
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(getPatientQueryKey({ workplaceId, patientId: newPatient.id }));
    },
    ...options,
  });
}
